import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Search, Trash2, X, Upload, Flame, Users } from "lucide-react";
import EmptyState from "@/components/EmptyState";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const STATUS_OPTIONS = ["lead", "prospect", "customer", "archived"];

export default function Contacts() {
  const [contacts, setContacts] = useState([]);
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(defaultForm());

  function defaultForm() {
    return { first_name: "", last_name: "", email: "", phone: "", company: "", tags: "", status: "lead", notes: "", source: "manual" };
  }

  const load = async () => {
    try {
      const { data } = await api.get("/contacts", { params: { q, status_filter: statusFilter } });
      setContacts(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  useEffect(() => {
    const t = setTimeout(load, 200);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q, statusFilter]);

  const submit = async (e) => {
    e.preventDefault();
    const payload = { ...form, tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean) };
    try {
      await api.post("/contacts", payload);
      toast.success("Contact created");
      setCreating(false);
      setForm(defaultForm());
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this contact?")) return;
    try {
      await api.delete(`/contacts/${id}`);
      setContacts((prev) => prev.filter((c) => c.id !== id));
      toast.success("Deleted");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="contacts-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <div className="section-label">People</div>
          <h1 className="font-heading font-black text-4xl md:text-5xl tracking-tighter uppercase mt-1">Contacts</h1>
          <p className="text-[var(--peak-muted)] mt-1 text-sm">{contacts.length} total in your workspace</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <label className="border-2 border-[var(--peak-ink)] hover:bg-black hover:text-white font-bold uppercase tracking-widest px-4 py-3 inline-flex items-center gap-2 cursor-pointer" data-testid="import-csv-label">
            <Upload className="w-4 h-4" /> Import CSV
            <input
              type="file"
              accept=".csv"
              className="hidden"
              data-testid="import-csv-input"
              onChange={async (e) => {
                const f = e.target.files?.[0];
                if (!f) return;
                const fd = new FormData();
                fd.append("file", f);
                try {
                  const { data } = await api.post("/contacts/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
                  toast.success(`Imported ${data.created} contact${data.created === 1 ? "" : "s"} (${data.skipped} skipped)`);
                  load();
                } catch (err) {
                  toast.error(formatApiError(err.response?.data?.detail) || "Import failed");
                } finally {
                  e.target.value = "";
                }
              }}
            />
          </label>
          <button
            data-testid="new-contact-btn"
            onClick={() => setCreating(true)}
            className="bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase tracking-widest px-6 py-3 inline-flex items-center gap-2 transition-colors"
          >
            <Plus className="w-4 h-4" /> New contact
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-0 top-1/2 -translate-y-1/2 text-[var(--peak-muted)]" />
          <input
            data-testid="contacts-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, email, phone or company"
            className="brutalist-input pl-6"
          />
        </div>
        <div className="flex items-center gap-1 border border-[var(--peak-border)] bg-white">
          {["", ...STATUS_OPTIONS].map((s) => (
            <button
              key={s || "all"}
              data-testid={`filter-status-${s || "all"}`}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${
                statusFilter === s ? "bg-[var(--peak-ink)] text-white" : "hover:bg-[var(--peak-surface-alt)]"
              }`}
            >
              {s || "All"}
            </button>
          ))}
        </div>
      </div>

      <div className="brutalist-card p-0 overflow-hidden">
        <table className="w-full" data-testid="contacts-table">
          <thead>
            <tr className="border-b-2 border-[var(--peak-ink)]">
              <th className="text-left text-xs font-bold uppercase tracking-wider text-[var(--peak-muted)] px-4 py-3">Name</th>
              <th className="text-left text-xs font-bold uppercase tracking-wider text-[var(--peak-muted)] px-4 py-3">Company</th>
              <th className="text-left text-xs font-bold uppercase tracking-wider text-[var(--peak-muted)] px-4 py-3">Email</th>
              <th className="text-left text-xs font-bold uppercase tracking-wider text-[var(--peak-muted)] px-4 py-3">Phone</th>
              <th className="text-left text-xs font-bold uppercase tracking-wider text-[var(--peak-muted)] px-4 py-3">Status</th>
              <th className="text-left text-xs font-bold uppercase tracking-wider text-[var(--peak-muted)] px-4 py-3">Score</th>
              <th className="text-left text-xs font-bold uppercase tracking-wider text-[var(--peak-muted)] px-4 py-3">Tags</th>
              <th className="text-right text-xs font-bold uppercase tracking-wider text-[var(--peak-muted)] px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {contacts.length === 0 && (
              <tr><td colSpan={8} className="py-6">
                <EmptyState
                  icon={Users}
                  title="No contacts yet"
                  hint="Add your first contact or import from CSV"
                  testId="contacts-empty"
                />
              </td></tr>
            )}
            {contacts.map((c) => (
              <tr key={c.id} className="peak-row border-b border-[var(--peak-border)] group" data-testid={`contact-row-${c.id}`}>
                <td className="px-4 py-3 text-sm font-semibold">
                  <Link className="hover:text-[var(--peak-accent)]" to={`/contacts/${c.id}`} data-testid={`contact-link-${c.id}`}>
                    {(c.first_name || "") + " " + (c.last_name || "") || "—"}
                  </Link>
                </td>
                <td className="px-4 py-3 text-sm">{c.company || "—"}</td>
                <td className="px-4 py-3 text-sm">{c.email || "—"}</td>
                <td className="px-4 py-3 text-sm">{c.phone || "—"}</td>
                <td className="px-4 py-3 text-sm">
                  <span className="px-2 py-1 text-xs font-bold uppercase tracking-wide bg-[var(--peak-surface-alt)] border border-[var(--peak-border)]">{c.status}</span>
                </td>
                <td className="px-4 py-3 text-sm">
                  {typeof c.score === "number" ? (
                    <div className="flex items-center gap-1" data-testid={`contact-score-${c.id}`}>
                      {c.score >= 70 && <Flame className="w-3.5 h-3.5 text-[var(--peak-accent)]" />}
                      <span className={`font-heading font-black text-sm ${c.score >= 70 ? "text-[var(--peak-accent)]" : c.score >= 40 ? "text-[var(--peak-ink)]" : "text-[var(--peak-muted)]"}`}>{c.score}</span>
                    </div>
                  ) : <span className="text-xs text-[var(--peak-muted)]">—</span>}
                </td>
                <td className="px-4 py-3 text-sm">
                  <div className="flex gap-1 flex-wrap">
                    {(c.tags || []).map((t) => (
                      <span key={t} className="px-2 py-0.5 text-xs bg-[var(--peak-blue-deep)] text-white font-bold uppercase tracking-wider">{t}</span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-right">
                  <button
                    data-testid={`delete-contact-${c.id}`}
                    onClick={() => remove(c.id)}
                    className="opacity-0 group-hover:opacity-100 hover:text-[#E50000] transition-opacity"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent className="bg-white border-2 border-[var(--peak-ink)] rounded-none max-w-lg" data-testid="new-contact-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-black text-2xl tracking-tighter uppercase">New contact</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-2 gap-4">
            {["first_name", "last_name", "email", "phone", "company"].map((k) => (
              <div key={k} className={k === "company" || k === "email" ? "col-span-2" : ""}>
                <label className="section-label">{k.replace("_", " ")}</label>
                <input
                  data-testid={`new-contact-${k}`}
                  className="brutalist-input"
                  value={form[k]}
                  onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                />
              </div>
            ))}
            <div>
              <label className="section-label">Status</label>
              <select
                data-testid="new-contact-status"
                className="brutalist-input"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="section-label">Tags (comma-separated)</label>
              <input
                data-testid="new-contact-tags"
                className="brutalist-input"
                value={form.tags}
                onChange={(e) => setForm({ ...form, tags: e.target.value })}
              />
            </div>
            <div className="col-span-2">
              <label className="section-label">Notes</label>
              <textarea
                data-testid="new-contact-notes"
                rows={3}
                className="w-full border border-[var(--peak-border)] p-2 focus:outline-none focus:border-[var(--peak-ink)]"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
              />
            </div>
            <DialogFooter className="col-span-2 flex gap-2 sm:justify-end">
              <button type="button" onClick={() => setCreating(false)} className="px-4 py-2 border-2 border-[var(--peak-ink)] font-bold uppercase text-xs tracking-widest hover:bg-black hover:text-white flex items-center gap-2" data-testid="cancel-new-contact">
                <X className="w-4 h-4" /> Cancel
              </button>
              <button type="submit" className="px-6 py-2 bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase text-xs tracking-widest" data-testid="submit-new-contact">
                Create
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
